import { computed, DestroyRef, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { CurrentUserService } from '@core/account-logic';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  type Step,
  WalkthroughState,
  WalkthroughTooltipComponent,
  WalkthroughStepsService,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class WalkthroughRecentNotesService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly featureSpecFacade = inject(FeatureSpecFacade);
  private readonly router = inject(Router);

  private recentNotesTabUsed = toSignal(
    this.currentUserService.featureDiscovered('recentNotesTabUsed'),
  );

  private hasNewDashboards = this.featureSpecFacade.selectIsEnabled(FeatureCode.Dashboards);

  private steps: Signal<Step[]> = computed(() => [
    {
      id: 'walkthrough-step-notes-1',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'top'],
          heading: this.i18n.translate('app.dashboard.recent_notes.walkthrough.heading'),
          description: this.i18n.translate('app.dashboard.recent_notes.walkthrough.description'),
          ctaText: this.i18n.translate('app.dashboard.recent_notes.walkthrough.cta_text'),
          showStepCounter: false,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,

        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 24, offsetY: 24 },
      },
      isHidden: !this.hasNewDashboards(),
    },
    {
      id: 'walkthrough-step-notes-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate('app.dashboard.recent_notes.walkthrough.heading'),
          description: this.i18n.translate('app.dashboard.recent_notes.walkthrough.description'),
          ctaText: this.i18n.translate('app.dashboard.recent_notes.walkthrough.cta_text'),
          showStepCounter: false,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 0, offsetY: 0 },
      },
      isHidden: this.hasNewDashboards(),
    },
  ]);

  public start(): void {
    if (!this.recentNotesTabUsed()) {
      this.walkthroughStepsService
        .start(this.getFilteredSteps())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(state => {
          if ([WalkthroughState.Finished, WalkthroughState.Canceled].includes(state)) {
            this.store.dispatch(
              FeatureDiscoveryActions.update({ payload: { recentNotesTabUsed: true } }),
            );
          }
          if (state === WalkthroughState.Finished) {
            this.router.navigate(['/dashboard/notes']);
          }
        });
    }
  }

  public refreshTooltip() {
    this.walkthroughStepsService.updateOverlayPositions();
  }

  private getFilteredSteps(): Step[] {
    return this.steps()
      .filter(step => !step.isHidden)
      .map((step, i) => ({ ...step, position: i + 1 }));
  }
}
